// src/pages/consulting/ConsultingContact.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import ConsultingLayout from "./ConsultingLayout.js";

import {
  Send,
  Calendar,
  Mail,
  MapPin,
  Phone,
  Check,
  Sparkles,
  ArrowRight,
  X,
  Home,
} from "lucide-react";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .cc-page {
    --bg:        #04050a;
    --surface:   #080b12;
    --surface-2: #0d1018;
    --border:    rgba(255,255,255,0.06);
    --border-2:  rgba(255,255,255,0.10);
    --accent:    #6366f1;
    --accent-2:  #8b5cf6;
    --green:     #10b981;
    --white:     #f8fafc;
    --muted:     #475569;
    --muted-2:   #64748b;
    --text:      #cbd5e1;
    --danger:    #ef4444;

    background: var(--bg);
    color: var(--white);
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  /* ── Background ── */
  .cc-bg-canvas {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .cc-bg-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(99,102,241,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(99,102,241,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%);
  }

  .cc-bg-orb-1 {
    position: absolute;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    width: 800px; height: 600px;
    background: radial-gradient(ellipse, rgba(99,102,241,0.09) 0%, transparent 70%);
    animation: cc-orb-pulse 8s ease-in-out infinite;
  }

  .cc-bg-orb-2 {
    position: absolute;
    top: 30%; right: -150px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(139,92,246,0.05) 0%, transparent 70%);
    animation: cc-orb-pulse 12s ease-in-out infinite reverse;
  }

  .cc-bg-orb-3 {
    position: absolute;
    bottom: 0; left: -100px;
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(59,130,246,0.05) 0%, transparent 70%);
    animation: cc-orb-pulse 10s ease-in-out infinite;
  }

  @keyframes cc-orb-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .cc-mouse-glow {
    position: fixed;
    width: 500px; height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
    transform: translate(-50%, -50%);
    transition: left 0.2s ease-out, top 0.2s ease-out;
  }

  /* ── Shell ── */
  .cc-shell {
    position: relative;
    z-index: 1;
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 32px;
  }

  /* ── Header ── */
  .cc-header {
    padding: 72px 0 56px;
    text-align: center;
    animation: cc-fade-up 0.6s ease both;
  }

  @keyframes cc-fade-up {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .cc-header-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--muted-2);
    margin-bottom: 24px;
  }

  .cc-header-tag-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--accent);
    box-shadow: 0 0 8px rgba(99,102,241,0.8);
    animation: cc-dot-blink 2s ease-in-out infinite;
  }

  @keyframes cc-dot-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  .cc-h1 {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: clamp(2.4rem, 6vw, 4rem);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.04em;
    margin-bottom: 20px;
    color: var(--white);
  }

  .cc-h1 span {
    background: linear-gradient(135deg, #a5b4fc 0%, #818cf8 40%, #6366f1 70%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .cc-header-sub {
    color: var(--muted-2);
    max-width: 480px;
    margin: 0 auto;
    font-size: 15.5px;
    line-height: 1.65;
  }

  /* ── Grid ── */
  .cc-content {
    padding-bottom: 100px;
    animation: cc-fade-up 0.6s 0.1s ease both;
  }

  .cc-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
  }

  @media (min-width: 1024px) {
    .cc-grid { grid-template-columns: 1.15fr 0.85fr; gap: 20px; }
  }

  /* ── Cards ── */
  .cc-card {
    border-radius: 20px;
    border: 1px solid var(--border);
    background: var(--surface);
    position: relative;
    overflow: hidden;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }

  .cc-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.4), transparent);
    opacity: 0;
    transition: opacity 0.3s;
  }

  .cc-card:hover { border-color: rgba(99,102,241,0.18); box-shadow: 0 24px 60px rgba(0,0,0,0.4); }
  .cc-card:hover::before { opacity: 1; }

  .cc-card-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 80% 60% at 50% 110%, rgba(99,102,241,0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  .cc-card-inner {
    padding: 28px 32px;
    position: relative;
    z-index: 1;
  }

  .cc-card-head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--border);
  }

  .cc-card-icon {
    width: 46px; height: 46px;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.2);
    color: #a5b4fc;
    flex-shrink: 0;
  }

  .cc-card-icon.purple {
    background: rgba(139,92,246,0.12);
    border-color: rgba(139,92,246,0.2);
    color: #c4b5fd;
  }

  .cc-card-title {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--white);
    margin-bottom: 3px;
  }

  .cc-card-sub { font-size: 12.5px; color: var(--muted-2); }

  /* ── Form ── */
  .cc-form { display: grid; gap: 16px; }

  .cc-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted-2);
    margin-bottom: 8px;
  }

  .cc-field {
    width: 100%;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.03);
    color: var(--white);
    padding: 12px 16px;
    outline: none;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .cc-field::placeholder { color: var(--muted); }
  .cc-field:hover { border-color: rgba(255,255,255,0.12); }
  .cc-field:focus {
    border-color: rgba(99,102,241,0.5);
    background: rgba(99,102,241,0.04);
    box-shadow: 0 0 0 4px rgba(99,102,241,0.1);
  }

  .cc-field:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* ── Buttons ── */
  .cc-btn-primary {
    width: 100%;
    border: none;
    border-radius: 12px;
    padding: 14px 20px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: white;
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    box-shadow: 0 0 30px rgba(99,102,241,0.25), 0 4px 16px rgba(0,0,0,0.3);
    cursor: pointer;
    transition: all 0.25s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    letter-spacing: 0.01em;
  }

  .cc-btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 0 50px rgba(99,102,241,0.4), 0 8px 24px rgba(0,0,0,0.4);
  }

  .cc-btn-primary:active:not(:disabled) { transform: translateY(0); }

  .cc-btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .cc-btn-primary.purple {
    background: linear-gradient(135deg, var(--accent-2), #6366f1);
    box-shadow: 0 0 30px rgba(139,92,246,0.25), 0 4px 16px rgba(0,0,0,0.3);
  }

  .cc-btn-primary.purple:hover {
    box-shadow: 0 0 50px rgba(139,92,246,0.4), 0 8px 24px rgba(0,0,0,0.4);
  }

  .cc-btn-success {
    background: linear-gradient(135deg, #059669, #10b981) !important;
    box-shadow: 0 0 30px rgba(16,185,129,0.3), 0 4px 16px rgba(0,0,0,0.3) !important;
  }

  .cc-btn-danger {
    background: linear-gradient(135deg, #dc2626, #ef4444) !important;
    box-shadow: 0 0 30px rgba(239,68,68,0.28), 0 4px 16px rgba(0,0,0,0.3) !important;
  }

  .cc-note {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--muted);
    margin-top: 4px;
  }

  .cc-error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #fca5a5;
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.18);
    padding: 10px 12px;
    border-radius: 12px;
  }

  /* ── Contact methods ── */
  .cc-method {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px;
    border-radius: 14px;
    border: 1px solid transparent;
    transition: all 0.2s ease;
    cursor: default;
  }

  .cc-method:hover {
    background: rgba(255,255,255,0.03);
    border-color: var(--border);
  }

  .cc-method-icon {
    width: 42px; height: 42px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .cc-method-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--muted);
    margin-bottom: 3px;
  }

  .cc-method-value { font-size: 14px; font-weight: 600; color: var(--white); }

  .cc-divider { height: 1px; background: var(--border); margin: 8px 0; }

  .cc-services-link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.02);
    color: var(--white);
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.25s ease;
    margin-top: 4px;
  }

  .cc-services-link:hover {
    background: rgba(99,102,241,0.06);
    border-color: rgba(99,102,241,0.25);
    transform: translateY(-2px);
  }

  .cc-services-link-icon {
    width: 28px; height: 28px;
    border-radius: 8px;
    background: rgba(99,102,241,0.12);
    border: 1px solid rgba(99,102,241,0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #a5b4fc;
    transition: all 0.25s ease;
  }

  .cc-services-link:hover .cc-services-link-icon {
    background: rgba(99,102,241,0.2);
    border-color: rgba(99,102,241,0.4);
  }

  /* ── FAB Home ── */
  .cc-fab-home {
    position: fixed;
    bottom: 32px;
    left: 32px;
    z-index: 1000;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2d1f6e, #3b2d8a);
    border: 1px solid rgba(139,92,246,0.35);
    box-shadow: 0 8px 32px rgba(99,102,241,0.3), 0 4px 16px rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.23,1,0.32,1);
    backdrop-filter: blur(12px);
  }

  .cc-fab-home:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 0 40px rgba(99,102,241,0.5), 0 8px 32px rgba(0,0,0,0.5);
    border-color: rgba(139,92,246,0.6);
    background: linear-gradient(135deg, #3b2d8a, #4c3aaa);
    color: #fff;
  }

  .cc-fab-home:active { transform: scale(0.97); }

  /* ── Scroll Navigator ── */
  .cc-scroll-nav {
    position: fixed;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .cc-scroll-btn {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: rgba(13,16,24,0.85);
    border: 1px solid rgba(255,255,255,0.08);
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    color: rgba(255,255,255,0.5);
    outline: none;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  }

  .cc-scroll-btn:hover {
    background: rgba(99,102,241,0.15);
    border-color: rgba(99,102,241,0.35);
    color: #818cf8;
    transform: scale(1.08);
    box-shadow: 0 0 20px rgba(99,102,241,0.2), 0 4px 20px rgba(0,0,0,0.4);
  }

  .cc-scroll-btn.disabled { opacity: 0.25; pointer-events: none; }

  .cc-scroll-btn svg {
    width: 16px; height: 16px;
    stroke: currentColor;
    stroke-width: 2.2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .cc-scroll-track {
    width: 2px; height: 80px;
    background: rgba(255,255,255,0.06);
    border-radius: 2px;
    overflow: hidden;
    position: relative;
  }

  .cc-scroll-fill {
    width: 100%;
    background: linear-gradient(180deg, #6366f1, #8b5cf6);
    border-radius: 2px;
    transition: height 0.1s linear;
    box-shadow: 0 0 8px rgba(99,102,241,0.6);
    position: absolute;
    top: 0; left: 0;
  }

  /* ── Modal ── */
  .cc-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(4,5,10,0.8);
    backdrop-filter: blur(12px);
    animation: cc-fade-in 0.2s ease;
  }

  @keyframes cc-fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  .cc-modal {
    width: min(980px, 96vw);
    border-radius: 24px;
    border: 1px solid rgba(99,102,241,0.2);
    background: var(--surface);
    box-shadow: 0 40px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(99,102,241,0.08);
    overflow: hidden;
    animation: cc-modal-in 0.3s cubic-bezier(0.16,1,0.3,1) both;
    position: relative;
  }

  .cc-modal::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(99,102,241,0.6), transparent);
  }

  @keyframes cc-modal-in {
    from { opacity: 0; transform: scale(0.96) translateY(12px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  .cc-modal-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid var(--border);
    background: var(--surface-2);
  }

  .cc-modal-badge { display: flex; align-items: center; gap: 12px; }

  .cc-modal-icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: rgba(139,92,246,0.15);
    border: 1px solid rgba(139,92,246,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c4b5fd;
  }

  .cc-modal-title {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--white);
    margin-bottom: 2px;
  }

  .cc-modal-sub { font-size: 12px; color: var(--muted-2); }

  .cc-modal-close {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    border: 1px solid var(--border);
    color: var(--muted-2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    outline: none;
  }

  .cc-modal-close:hover {
    background: rgba(255,255,255,0.08);
    border-color: rgba(255,255,255,0.15);
    color: var(--white);
  }

  /* ── Success popup ── */
  .cc-success-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(4,5,10,0.72);
    backdrop-filter: blur(10px);
    animation: cc-fade-in 0.2s ease;
  }

  .cc-success-modal {
    width: min(460px, 94vw);
    border-radius: 22px;
    border: 1px solid rgba(16,185,129,0.25);
    background: linear-gradient(180deg, rgba(8,11,18,0.98), rgba(13,16,24,0.98));
    box-shadow: 0 30px 90px rgba(0,0,0,0.65), 0 0 0 1px rgba(16,185,129,0.08);
    padding: 28px 24px 22px;
    text-align: center;
    animation: cc-modal-in 0.28s cubic-bezier(0.16,1,0.3,1) both;
    position: relative;
    overflow: hidden;
  }

  .cc-success-modal::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(16,185,129,0.7), transparent);
  }

  .cc-success-icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 18px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(16,185,129,0.12);
    border: 1px solid rgba(16,185,129,0.22);
    color: #34d399;
    box-shadow: 0 0 30px rgba(16,185,129,0.12);
  }

  .cc-success-title {
    font-family: 'Bricolage Grotesque', sans-serif;
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: var(--white);
    margin-bottom: 10px;
  }

  .cc-success-text {
    color: var(--muted-2);
    font-size: 14px;
    line-height: 1.7;
    margin-bottom: 20px;
  }

  .cc-success-btn {
    border: none;
    border-radius: 12px;
    padding: 12px 18px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: white;
    background: linear-gradient(135deg, #059669, #10b981);
    box-shadow: 0 0 24px rgba(16,185,129,0.22), 0 4px 16px rgba(0,0,0,0.3);
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .cc-success-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 34px rgba(16,185,129,0.32), 0 8px 22px rgba(0,0,0,0.35);
  }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .cc-card-inner { padding: 22px 20px; }
    .cc-scroll-nav { display: none; }
    .cc-fab-home { bottom: 20px; left: 20px; width: 46px; height: 46px; }
  }
`;

/* ── Scroll Navigator ── */
function ScrollNavigator() {
  const [progress, setProgress] = useState(0);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(pct);
      setAtTop(scrollTop <= 10);
      setAtBottom(pct >= 98);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="cc-scroll-nav">
      <button
        className={`cc-scroll-btn ${atTop ? "disabled" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
      </button>
      <div className="cc-scroll-track">
        <div className="cc-scroll-fill" style={{ height: `${progress}%` }} />
      </div>
      <button
        className={`cc-scroll-btn ${atBottom ? "disabled" : ""}`}
        onClick={() =>
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
          })
        }
      >
        <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
    </div>
  );
}

const CONTACT_METHODS = [
  {
    icon: Mail,
    label: "Email",
    value: "hadycomtech@gmail.com",
    color: "linear-gradient(135deg,#3b82f6,#22d3ee)",
    bg: "rgba(59,130,246,0.12)",
  },
  {
    icon: MapPin,
    label: "Localisation",
    value: "France, Guinée ",
    color: "linear-gradient(135deg,#8b5cf6,#a78bfa)",
    bg: "rgba(139,92,246,0.12)",
  },
  {
    icon: Phone,
    label: "Téléphone",
    value: "+33 7 51 24 92 33, +224 623 84 92 46",
    color: "linear-gradient(135deg,#10b981,#34d399)",
    bg: "rgba(16,185,129,0.12)",
  },
];

const EMAILJS_SERVICE_ID = "service_0q9nbi7";
const EMAILJS_TEMPLATE_ID = "template_7jwdfag";
const EMAILJS_PUBLIC_KEY = "qt3pXhwa5ythBANkI";
const RECEIVER_EMAIL = "hadycomtech@gmail.com";

export default function ConsultingContact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: -999, y: -999 });

  useEffect(() => {
    const onMouse = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsCalendlyOpen(false);
        setIsSuccessOpen(false);
      }
    };

    window.addEventListener("mousemove", onMouse);
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isCalendlyOpen || isSuccessOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isCalendlyOpen, isSuccessOpen]);

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (isSending) return;

  setSendError("");
  setIsSending(true);

  try {
    console.log("SERVICE:", EMAILJS_SERVICE_ID);
console.log("TEMPLATE:", EMAILJS_TEMPLATE_ID);
console.log("PUBLIC KEY:", EMAILJS_PUBLIC_KEY);

const result = await emailjs.send(
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  {
    from_name: formData.name,
    from_email: formData.email,
    message: formData.message,
  },
  {
    publicKey: EMAILJS_PUBLIC_KEY,
  }
);

    console.log("SUCCESS:", result);

    setIsSubmitted(true);
    setIsSuccessOpen(true);

    setFormData({
      name: "",
      email: "",
      message: "",
    });

    setTimeout(() => setIsSubmitted(false), 3000);
  } catch (error) {
    console.error("Erreur envoi EmailJS :", error);
    setSendError(
      `L’envoi du message a échoué : ${error?.text || error?.message || "erreur inconnue"}`
    );
  } finally {
    setIsSending(false);
  }
};

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    if (sendError) {
      setSendError("");
    }
  };

  return (
    <ConsultingLayout>
      <style>{STYLES}</style>
      <ScrollNavigator />

      <main className="cc-page">
        {/* ── Background ── */}
        <div className="cc-bg-canvas">
          <div className="cc-bg-grid" />
          <div className="cc-bg-orb-1" />
          <div className="cc-bg-orb-2" />
          <div className="cc-bg-orb-3" />
        </div>

        {/* ── Mouse glow ── */}
        <div
          className="cc-mouse-glow"
          style={{ left: mousePos.x, top: mousePos.y }}
        />

        <div className="cc-shell">
          {/* ── Header ── */}
          <header className="cc-header">
            <div className="cc-header-tag">
              <span className="cc-header-tag-dot" />
              Parlons de votre projet
            </div>
            <h1 className="cc-h1">
              Contactez-<span>nous.</span>
            </h1>
            <p className="cc-header-sub">
              Présentez votre besoin. Nous revenons vers vous rapidement
              avec un prochain jalon clair et concret.
              <br />
              Ou rejoignez-nous pour construire ensemble une future collaboration.
            </p>
          </header>

          {/* ── Content ── */}
          <div className="cc-content">
            <div className="cc-grid">
              {/* ── Form card ── */}
              <div className="cc-card">
                <div className="cc-card-glow" />
                <div className="cc-card-inner">
                  <div className="cc-card-head">
                    <div className="cc-card-icon">
                      <Send size={20} />
                    </div>
                    <div>
                      <div className="cc-card-title">Demande de contact</div>
                      <div className="cc-card-sub">
                        Un brief clair → une réponse rapide.
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="cc-form">
                    <div>
                      <label className="cc-label">Nom / Organisation</label>
                      <input
                        className="cc-field"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="HDC Consulting / Jean Dupont"
                        required
                        disabled={isSending}
                      />
                    </div>

                    <div>
                      <label className="cc-label">Email</label>
                      <input
                        className="cc-field"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="nom@entreprise.com"
                        required
                        disabled={isSending}
                      />
                    </div>

                    <div>
                      <label className="cc-label">Message</label>
                      <textarea
                        className="cc-field"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Contexte, objectifs, périmètre, délai…"
                        required
                        disabled={isSending}
                        style={{ minHeight: 148, resize: "none" }}
                      />
                    </div>

                    {sendError && (
                      <div className="cc-error">
                        <span>⚠️</span>
                        <span>{sendError}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSending}
                      className={`cc-btn-primary ${
                        isSubmitted
                          ? "cc-btn-success"
                          : sendError
                          ? "cc-btn-danger"
                          : ""
                      }`}
                    >
                      {isSending ? (
                        <>Envoi en cours...</>
                      ) : isSubmitted ? (
                        <>
                          <Check size={17} /> Message envoyé !
                        </>
                      ) : (
                        <>
                          Envoyer le message <Send size={15} />
                        </>
                      )}
                    </button>

                    <div className="cc-note">
                      <Sparkles size={13} /> Réponse garantie sous 24h (jours ouvrés)
                    </div>
                  </form>
                </div>
              </div>

              {/* ── Right column ── */}
              <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
                {/* Calendly card */}
                <div className="cc-card">
                  <div className="cc-card-glow" />
                  <div className="cc-card-inner">
                    <div className="cc-card-head">
                      <div className="cc-card-icon purple">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <div className="cc-card-title">Planifier un échange</div>
                        <div className="cc-card-sub">
                          30 min pour cadrer votre besoin.
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="cc-btn-primary purple"
                      onClick={() => setIsCalendlyOpen(true)}
                    >
                      Réserver un créneau <ArrowRight size={15} />
                    </button>
                  </div>
                </div>

                {/* Coordinates card */}
                <div className="cc-card">
                  <div className="cc-card-glow" />
                  <div className="cc-card-inner">
                    <div className="cc-card-title" style={{ marginBottom: 16 }}>
                      Nos coordonnées
                    </div>

                    <div style={{ display: "grid", gap: 4 }}>
                      {CONTACT_METHODS.map((m, i) => {
                        const Icon = m.icon;
                        return (
                          <div className="cc-method" key={i}>
                            <div
                              className="cc-method-icon"
                              style={{ background: m.bg, border: `1px solid ${m.bg}` }}
                            >
                              <Icon
                                size={17}
                                style={{
                                  background: m.color,
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }}
                              />
                            </div>
                            <div>
                              <div className="cc-method-label">{m.label}</div>
                              <div className="cc-method-value">{m.value}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="cc-divider" />

                    <Link to="/consulting/services" className="cc-services-link">
                      <span>Découvrir nos services</span>
                      <div className="cc-services-link-icon">
                        <ArrowRight size={14} />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── FAB Home ── */}
        <Link
          to="/consulting"
          className="cc-fab-home"
          aria-label="Retour à l'accueil consulting"
          title="Accueil Consulting"
        >
          <Home size={20} />
        </Link>

        {/* ── Calendly modal ── */}
        {isCalendlyOpen && (
          <div
            className="cc-modal-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setIsCalendlyOpen(false)}
          >
            <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cc-modal-head">
                <div className="cc-modal-badge">
                  <div className="cc-modal-icon">
                    <Calendar size={17} />
                  </div>
                  <div>
                    <div className="cc-modal-title">
                      Échange découverte – HDC Consulting
                    </div>
                    <div className="cc-modal-sub">
                      Sélectionnez un créneau disponible
                    </div>
                  </div>
                </div>
                <button
                  className="cc-modal-close"
                  onClick={() => setIsCalendlyOpen(false)}
                  aria-label="Fermer"
                >
                  <X size={16} />
                </button>
              </div>
              <div style={{ height: "75vh", background: "#fff" }}>
                <iframe
                  title="Calendly"
                  src="https://calendly.com/modyhady94/30min?hide_gdpr_banner=1"
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Success popup ── */}
        {isSuccessOpen && (
          <div
            className="cc-success-overlay"
            role="dialog"
            aria-modal="true"
            onClick={() => setIsSuccessOpen(false)}
          >
            <div
              className="cc-success-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cc-success-icon-wrap">
                <Check size={30} />
              </div>

              <h3 className="cc-success-title">Merci pour votre message</h3>

              <p className="cc-success-text">
                Merci d’avoir pris le temps de nous envoyer un message.
                <br />
                Notre équipe reviendra vers vous rapidement sur{" "}
                <strong>{RECEIVER_EMAIL}</strong> avec un retour clair et concret.
              </p>

              <button
                type="button"
                className="cc-success-btn"
                onClick={() => setIsSuccessOpen(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </main>

      
    </ConsultingLayout>
  );
}